import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import Search from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { GetOrgMembersMemberResponseDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { createFetchSubscriptionOrgMembersQueryFn } from 'app/queries/subscriptions/createFetchSubscriptionOrgMembersQueryFn';
import { subscriptionsKeys } from 'app/queries/subscriptions/subscriptionsKeys';
import React from 'react';
import { Content } from './styles/Content';

type TProps = {
  baId: string;
  subId: string;
  onCancel: () => void;
  onSuccess: (assignee: GetOrgMembersMemberResponseDto | null) => void;
};

export const AddUserLicenseModal = ({ onCancel, onSuccess, baId, subId }: TProps) => {
  const { call } = useAPI();
  const [search, setSearch] = React.useState('');
  const [assignee, setAssignee] = React.useState<GetOrgMembersMemberResponseDto | null>(null);
  const debouncedSearch = useDebouncedValue(search, 500);
  const isSearchSettling = search !== debouncedSearch;

  const usersQuery = useQuery({
    queryKey: subscriptionsKeys.orgMembersList({ baId, subscriptionId: subId, search: debouncedSearch }),
    queryFn: createFetchSubscriptionOrgMembersQueryFn(call),
    enabled: !isSearchSettling,
    placeholderData: keepPreviousData,
  });
  useNotifyQueryError(usersQuery.error);
  const users = usersQuery.data?.data ?? [];

  const changeAssignee = (data: GetOrgMembersMemberResponseDto | null) => {
    if (!data) {
      setAssignee(null);
      return;
    }
    setAssignee(data);
  };

  const renderAssignee = () => {
    if (!assignee) {
      return;
    }
    return (
      <div className="assignTo">
        <div className="assignToText">
          <p>
            {assignee.name || ''} {assignee.lastname || ''}
          </p>
          {assignee.email ? <p>{assignee.email}</p> : ''}
        </div>
        <IconButtonDelete type="button" title="Clear assignee" onClick={() => changeAssignee(null)}>
          <TrashBin />
        </IconButtonDelete>
      </div>
    );
  };

  const handleSubmit = async () => {
    onSuccess(assignee);
  };

  return (
    <ModalWrapper width="487px">
      <ModalTitle>Add License to User</ModalTitle>
      <Content>
        {renderAssignee()}
        <Input
          className="assignee-input"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          label="Search user to assign"
          name="assignUser"
          icon={Search}
          value={search}
        />
        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((data) => (
                <Tr key={data.id}>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.name}
                    </p>
                  </Td>
                  <Td>
                    <p className="cursor-pointer" onClick={() => changeAssignee(data)}>
                      {data?.email}
                    </p>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={!assignee} onClick={handleSubmit} data-testid="add-user-license-button">
            Add User
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
